// MOONSHINE — theme.js: THE KRAFT LABEL skin (caP'n 2026-10-31: "B is perfect <33") + the keyframe injection law
// (id-guarded, and it REPLACES a stale node: a guard that only checks the id
//  keeps serving a previous build's animations after an in-place update).
//
// THE LOOK: every menu is a bootlegger's brown-paper label - kraft paper, typewriter ink, red rubber stamps,
// hung on twine, torn edges, an XXX here and there. The HUD's little chips are paper TAGS (punched hole, string).
// Everything is drawn in code (no image files, no drain). The fonts ship in public/fonts (@font-face injected below, under PUBLIC_URL):
// Sancreek (OFL) for the big hand-lettered titles, Special Elite (Apache 2.0) for the typewriter text.
//
// THE ONE RULE OF THIS FILE: chrome sits on PAPER, so its text is INK. Only a few words sit straight on the 3D
// woods (night line, key hints, friends' name tags) - those use the SCENE colours (cream with a shadow).
const PAL = {
  // MEASURED (review 2026-10-31): every text colour clears ~3:1 or better on this kraft (ink 6.6, dim 4.7) - small typewriter text needs it
  kraft: "#C49C66", kraftDeep: "#8A6338", ink: "#2A1A0E", inkSoft: "#4A3219", rust: "#5C3415",
  red: "#8E1A14", stamp: "#A3221B", green: "#1F4F1B", gold: "#6A4300", goldRing: "#D39A1A", cream: "#F3E6C4", twine: "#D8C79A", blue: "#1F5277",
};
// the paper itself: fine fibre noise + a lighter top-left + a darker bottom-right, on kraft brown
export const PAPER = [
  `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='300' height='300'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='.9' numOctaves='3' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 .35  0 0 0 0 .22  0 0 0 0 .1  0 0 0 .55 0'/></filter><rect width='300' height='300' filter='url(%23n)'/></svg>")`,
  "radial-gradient(ellipse at 30% 20%, rgba(255,240,200,.25), transparent 60%)",
  "radial-gradient(ellipse at 80% 90%, rgba(70,40,10,.16), transparent 60%)",
  PAL.kraft,
].join(", ");
export const C = {
  bg: "#0b0805", panel: PAPER,                                   // panel = a background string (use it with `background:`)
  edge: "rgba(42,26,14,.42)",                                    // a ruled ink line
  amber: "#7A4A1E", amberHi: PAL.rust,                           // accents: warm brown / rust (highlighted words, numbers)
  ink: PAL.ink, dim: PAL.inkSoft, good: PAL.green, bad: PAL.red,   // bad = deep red ink (3.6:1)
  night: "#CFD6FF",                                              // only ever on the 3D scene (the NIGHT line)
  font: "'Special Elite', 'Courier New', monospace",             // labels, chips, buttons (typewriter)
  body: "'Special Elite', 'Courier New', monospace",             // sentences
  display: "'Sancreek', 'Rye', Georgia, serif",                  // big titles only (panel names, the title screen)
  red: PAL.stamp, cream: PAL.cream,      // red = the STAMP (a fill / the big titles); bad = the darker red INK for words twine: PAL.twine, gold: PAL.gold, goldRing: PAL.goldRing, blue: PAL.blue, kraftDeep: PAL.kraftDeep,
  well: "rgba(243,230,196,.55)",                                 // a little paper well (a tile, a slot)
  track: "rgba(42,26,14,.18)",                                   // an empty bar / ring track on paper
  scene: PAL.cream, sceneHi: "#F6D58A",                          // words straight on the woods
  sceneShadow: "0 1px 3px #000, 0 0 8px rgba(0,0,0,.85)",
};
// a colour from the data (a pastel ingredient, a fraction) made readable as INK on kraft: mixed toward the ink
// It starts at mix k and keeps deepening until it reads (contrast >= MIN against the kraft) - a pale colour can never come out faint.
const lum = (h) => { const n = parseInt(h.slice(1), 16); return [(n >> 16) & 255, (n >> 8) & 255, n & 255].map((v) => { v /= 255; return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4; }).reduce((a, v, i) => a + v * [0.2126, 0.7152, 0.0722][i], 0); };
export const contrast = (a, b) => { const x = lum(a), y = lum(b); return (Math.max(x, y) + 0.05) / (Math.min(x, y) + 0.05); };
export function inked(hex, k = 0.35, MIN = 3.4) {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex || ""); if (!m) return PAL.inkSoft;
  const n = parseInt(m[1], 16), rgb = [(n >> 16) & 255, (n >> 8) & 255, n & 255], t = [0x2a, 0x1a, 0x0e];
  const mix = (q) => "#" + rgb.map((v, i) => Math.round(v + (t[i] - v) * q).toString(16).padStart(2, "0")).join("");
  let q = k, out = mix(q);
  while (q < 0.95 && contrast(out, PAL.kraft) < MIN) { q += 0.05; out = mix(q); }
  return out;
}
// THE TAG: a paper tag with a clipped point on the left and a punched hole (with the twine through it)
export const TAG_CLIP = "polygon(12px 0, 100% 0, 100% 100%, 12px 100%, 0 50%)";
export const HOLE = `radial-gradient(circle at 15px 50%, ${PAL.ink} 3px, ${PAL.twine} 4.2px, transparent 5px)`;
// THE STAMP: rubber-stamp ink is never solid - this speckles it (a mask, so it works on any colour)
export const STAMP_MASK = `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='120' height='60'><filter id='n'><feTurbulence baseFrequency='.55' numOctaves='2'/><feColorMatrix values='0 0 0 0 1  0 0 0 0 1  0 0 0 0 1  0 0 0 -2.2 2.1'/></filter><rect width='120' height='60' filter='url(%23n)'/></svg>")`;
// THE TORN EDGE: a deckled paper outline, deterministic per seed (same label = same tears every time)
const DECKLE = {};
export function deckle(seed = 7, n = 40, amp = 7) {
  const key = seed + ":" + n + ":" + amp; if (DECKLE[key]) return DECKLE[key];
  let x = seed; const r = () => (x = (x * 9301 + 49297) % 233280) / 233280, pts = [], side = Math.max(4, Math.round(n / 3));
  for (let i = 0; i <= n; i++) pts.push(`${((i / n) * 100).toFixed(2)}% ${(r() * amp).toFixed(1)}px`);
  for (let i = 0; i <= side; i++) pts.push(`calc(100% - ${(r() * amp).toFixed(1)}px) ${((i / side) * 100).toFixed(2)}%`);
  for (let i = n; i >= 0; i--) pts.push(`${((i / n) * 100).toFixed(2)}% calc(100% - ${(r() * amp).toFixed(1)}px)`);
  for (let i = side; i >= 0; i--) pts.push(`${(r() * amp).toFixed(1)}px ${((i / side) * 100).toFixed(2)}%`);
  return (DECKLE[key] = `polygon(${pts.join(", ")})`);
}
// msPulse lives INSIDE the element (a brightness breath + a hair of scale): a tag is clipped and a stamp is masked,
// so an outer glow would be cut off. msSway = a hanging tag nudged on its string.
const FONTS = (process.env.PUBLIC_URL || "") + "/fonts/";   // shipped with the game (no Google call, no drain); PUBLIC_URL like the icons
const CSS = `
@font-face { font-family: 'Sancreek'; src: url('${FONTS}Sancreek-Regular.ttf') format('truetype'); font-display: swap; }
@font-face { font-family: 'Special Elite'; src: url('${FONTS}SpecialElite-Regular.ttf') format('truetype'); font-display: swap; }
button:focus-visible { box-shadow: inset 0 0 0 2px #F3E6C4, inset 0 0 0 4px #2A1A0E !important; outline: none; }   /* the stamp mask clips an outline: the focus ring sits INSIDE */
@keyframes msFade { 0%{opacity:0; transform:translateY(6px)} 12%{opacity:1; transform:none} 85%{opacity:1} 100%{opacity:0} }
@keyframes msRise { from{opacity:0; transform:translateY(14px) rotate(-.6deg)} to{opacity:1; transform:none} }
@keyframes msFlash { 0%{opacity:.75} 100%{opacity:0} }
@keyframes msPulse { 0%,100%{filter:none; transform:scale(1)} 50%{filter:brightness(1.22) saturate(1.15); transform:scale(1.035)} }
@keyframes msSway { 0%,100%{transform:rotate(0deg)} 30%{transform:rotate(-2.2deg)} 70%{transform:rotate(1.4deg)} }
@keyframes msDrip { 0%{opacity:0; transform:translateY(-4px)} 30%{opacity:1} 100%{opacity:0; transform:translateY(8px)} }
@keyframes msShake { 0%,100%{transform:none} 25%{transform:translate(-3px,1px)} 50%{transform:translate(3px,-1px)} 75%{transform:translate(-2px,-1px)} }
`;
export function ensureKeyframes() {
  if (typeof document === "undefined") return;
  let el = document.getElementById("ms-keyframes");
  if (!el) { el = document.createElement("style"); el.id = "ms-keyframes"; document.head.appendChild(el); }
  if (el.textContent !== CSS) el.textContent = CSS;
}

// ── THE EMPIRE KIT's palette (2026-10-24, the Mark): GM + MANUSCRIPT_BG, VERBATIM from the Pier's theme.js.
// Only the shared kit reads these (AchievementBadgeBook); Moonshine's own chrome stays on C above.
export const GM = {
  // Page backgrounds — aged vellum, dark ink-stained
  page:          "#0f0c07",       // deepest ink-soaked vellum
  pageWarm:      "#1a1509",       // aged leather cover
  pageMid:       "#241c0c",       // worn parchment mid
  pageLight:     "#2e2410",       // candlelit page
  pageBorder:    "#3d2e14",       // manuscript margin line

  // Ink colors
  inkGold:       "#c8963c",       // iron gall gold — primary accent
  inkGoldBright: "#e8b84b",       // fresh gold illumination
  inkGoldDim:    "#8a6520",       // faded annotation
  inkBrown:      "#8a6a30",       // worn copper border
  inkRed:        "#8b2020",       // vermillion rubric

  // Parchment text
  vellum:        "#e8d5a3",       // warm candlelight cream — main text
  vellumDim:     "#b89a5a",       // aged gold ink — secondary
  vellumFaint:   "#7a6040",       // water-damaged — muted

  // Decorative
  ruling:        "rgba(139,106,48,0.18)", // faint ruling lines

  font:  "'Cinzel', 'Palatino Linotype', serif",
  fontD: "'Cinzel Decorative', 'Palatino Linotype', serif",
};

export const MANUSCRIPT_BG = [
  "repeating-linear-gradient(0deg, transparent, transparent 27px, rgba(139,106,48,0.05) 27px, rgba(139,106,48,0.05) 28px)",
  "radial-gradient(ellipse at 25% 15%, rgba(120,80,20,0.18) 0%, transparent 55%)",
  "radial-gradient(ellipse at 75% 85%, rgba(100,60,15,0.15) 0%, transparent 55%)",
  "#0f0c07",
].join(", ");

// YOUR LABEL (2026-10-31, ROADMAP 41): the 8 label colours (art digit -> colour). 0 = the paper (blank).
// Shared by the HUD's LabelCanvas and the trophy shelf's CanvasTexture (World3D) - one palette, one painter.
export const LABEL_PAL = ["#efe3bd", "#141414", "#c8102e", "#e8b030", "#6a3fb0", "#3a7bd5", "#4f9a4a", "#ffffff"];
export function drawLabel(ctx, art, W, H, cell) {
  ctx.fillStyle = LABEL_PAL[0]; ctx.fillRect(0, 0, W * cell, H * cell);
  if (typeof art !== "string" || art.length !== W * H) return;
  for (let i = 0; i < art.length; i++) {
    const c = art.charCodeAt(i) - 48;
    if (c <= 0 || c > 7) continue;
    ctx.fillStyle = LABEL_PAL[c]; ctx.fillRect((i % W) * cell, Math.floor(i / W) * cell, cell, cell);
  }
}
